import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useEffect, useCallback, useState, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { toast } from "sonner";
import {
  donationMasterAPI,
  statesAPI,
  districtsAPI,
  type DonationMaster,
  type DonationMasterCreatePayload,
  type State,
  type District,
  type City,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

type DonationFormState = {
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  message: string;
  organization: string;
  pan_number: string;
  mode: string;
  address_line1: string;
  address_line2: string;
  state: string;
  district: string;
  city: string;
  pincode: string;
  payment_mode: string;
  by_cheque_no: string;
};

const initialFormState: DonationFormState = {
  name: "",
  email: "",
  phone: "",
  donation_type: "",
  amount: "",
  message: "",
  organization: "",
  pan_number: "",
  mode: "offline",
  address_line1: "",
  address_line2: "",
  state: "",
  district: "",
  city: "",
  pincode: "",
  payment_mode: "",
  by_cheque_no: "",
};

function getErrorMessage(
  err: unknown,
  fallback: string = "Failed to save donation"
) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

export default function DonationMasterPage() {
  const [donations, setDonations] = useState<DonationMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DonationFormState>(initialFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<DonationMaster | null>(null);
  const [deleting, setDeleting] = useState<DonationMaster | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const list = await statesAPI.list();
        setStates(list);
      } catch {
        toast.error("Failed to load states");
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!form.state) {
        setDistricts([]);
        return;
      }
      setLoadingDistricts(true);
      try {
        const list = await statesAPI.getDistrictsByStateId(form.state);
        setDistricts(list);
      } catch {
        toast.error("Failed to load districts");
      } finally {
        setLoadingDistricts(false);
      }
    };
    loadDistricts();
  }, [form.state]);

  useEffect(() => {
    const loadCities = async () => {
      if (!form.district) {
        setCities([]);
        return;
      }
      setLoadingCities(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(form.district);
        setCities(list);
      } catch {
        toast.error("Failed to load cities");
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [form.district]);

  const resetForm = () => {
    setForm(initialFormState);
    setEditing(null);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await donationMasterAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setDonations(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load donations");
      setDonations([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Donor name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Invalid email address format");
      return;
    }
    if (
      !form.phone.trim() ||
      !/^[0-9]{10,15}$/.test(form.phone.replace(/[\s+-]/g, ""))
    ) {
      toast.error("Valid phone number is required");
      return;
    }
    if (!form.donation_type.trim()) {
      toast.error("Donation type is required");
      return;
    }
    if (
      !form.amount.trim() ||
      isNaN(Number(form.amount)) ||
      Number(form.amount) <= 0
    ) {
      toast.error("A valid donation amount is required");
      return;
    }
    if (
      !form.pan_number.trim() ||
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_number.toUpperCase())
    ) {
      toast.error("Valid 10-character PAN number is required");
      return;
    }
    if (!form.payment_mode) {
      toast.error("Payment mode is required");
      return;
    }

    if (!form.address_line1.trim()) {
      toast.error("Address Line 1 is required");
      return;
    }
    if (!form.state) {
      toast.error("State is required");
      return;
    }
    if (!form.district) {
      toast.error("District is required");
      return;
    }
    if (!form.city) {
      toast.error("City is required");
      return;
    }
    if (!form.pincode.trim() || !/^[0-9]{6}$/.test(form.pincode)) {
      toast.error("Valid 6-digit Pincode is required");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: DonationMasterCreatePayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        donation_type: form.donation_type.trim(),
        amount: Number(form.amount),
        message: form.message.trim(),
        organization: form.organization.trim(),
        pan_number: form.pan_number.trim(),
        mode: "offline",
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim(),
        state: form.state,
        district: form.district,
        city: form.city,
        pincode: form.pincode.trim(),
        payment_mode: form.payment_mode,
        by_cheque_no: form.by_cheque_no.trim() || undefined,
      };

      if (editing) {
        await donationMasterAPI.update(editing.id, payload);
        toast.success("Donation updated successfully");
      } else {
        await donationMasterAPI.create(payload);
        toast.success("Donation created successfully");
      }

      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err)));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }

    setDeleteLoading(true);
    try {
      await donationMasterAPI.delete(deleting.id);
      toast.success("Donation deleted successfully");
      setDeleting(null);
      if (donations.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err, "Failed to delete donation")));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback((donation: DonationMaster) => {
    setEditing(donation);
    setForm({
      name: donation.name ?? "",
      email: donation.email ?? "",
      phone: String(donation.phone ?? ""),
      donation_type: donation.donation_type ?? "",
      amount: String(donation.amount ?? ""),
      message: donation.message ?? "",
      organization: donation.organization ?? "",
      pan_number: donation.pan_number ?? "",
      mode: donation.mode ?? "",
      address_line1: donation.address_line1 ?? "",
      address_line2: donation.address_line2 ?? "",
      state: String(donation.state_id ?? donation.state ?? ""),
      district: String(donation.district_id ?? donation.district ?? ""),
      city: String(donation.city_id ?? donation.city ?? ""),
      pincode: donation.pincode ?? "",
      payment_mode: donation.payment_mode ?? "",
      by_cheque_no: donation.by_cheque_no ?? "",
    });
    setDialogOpen(true);
  }, []);

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<DonationMaster>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        showStatus: false,
        renderNameCell: row => (
          <>
            <span className="font-medium">{row.name}</span>
            {row.organization && (
              <div className="text-xs text-muted-foreground">
                {row.organization}
              </div>
            )}
          </>
        ),
        extraColumns: [
          {
            id: "email",
            label: "Email",
            width: 180,
            renderCell: row => row.email,
          },
          {
            id: "phone",
            label: "Phone",
            width: 120,
            renderCell: row => row.phone,
          },
          {
            id: "amount",
            label: "Amount",
            width: 100,
            renderCell: row => `₹${Number(row.amount).toFixed(2)}`,
          },
          {
            id: "type",
            label: "Type",
            width: 100,
            renderCell: row => (
              <span className="capitalize">{row.donation_type}</span>
            ),
          },
        ],
      }),
    [currentPage, pageSize, openEdit]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Donor List</h1>
        <p className="text-muted-foreground">Manage donor records</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-donation"
        tableColumns={tableColumns}
        listTitle={`Donations (${totalItems})`}
        searchPlaceholder="Search donations..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[min(90vh,780px)] w-full max-w-xl flex-col gap-0 overflow-hidden p-0 sm:max-w-xl [&>button]:right-4 [&>button]:top-4">
              <DialogHeader className="shrink-0 space-y-0 border-b border-border bg-background px-6 py-4 pr-12 text-left">
                <DialogTitle className="text-lg">
                  {editing ? "Edit Donation" : "Add New Donation"}
                </DialogTitle>
              </DialogHeader>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-6 py-4">
                <div>
                  <Label htmlFor="donation-name">Name *</Label>
                  <Input
                    id="donation-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Donor name"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="donation-email">Email *</Label>
                    <Input
                      id="donation-email"
                      type="email"
                      value={form.email}
                      onChange={e =>
                        setForm(prev => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="donor@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="donation-phone">Phone *</Label>
                    <Input
                      id="donation-phone"
                      value={form.phone}
                      onChange={e =>
                        setForm(prev => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder="Phone number"
                      maxLength={10}
                      onInput={e => {
                        e.currentTarget.value = e.currentTarget.value.replace(
                          /[^0-9]/g,
                          ""
                        );
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="donation-org">Organization/Company</Label>
                  <Input
                    id="donation-org"
                    value={form.organization}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        organization: e.target.value,
                      }))
                    }
                    placeholder="Organization name"
                  />
                </div>

                {/* <div className="">
                  <div>
                    <Label htmlFor="donation-type">Donation Type *</Label>
                    <Select
                      value={form.donation_type || undefined}
                      onValueChange={value =>
                        setForm(prev => ({ ...prev, donation_type: value }))
                      }
                    >
                      <SelectTrigger id="donation-type">
                        <SelectValue placeholder="Select donation type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="infrastructure">
                          Infrastructure
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="donation-amount">Donation Amount *</Label>
                  <Input
                    id="donation-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={e =>
                      setForm(prev => ({ ...prev, amount: e.target.value }))
                    }
                    placeholder="Amount"
                  />
                </div>

                <div>
                  <Label htmlFor="by-cheque-no">By cash/cheque no</Label>
                  <Input
                    id="by-cheque-no"
                    value={form.by_cheque_no}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        by_cheque_no: e.target.value,
                      }))
                    }
                    placeholder="Enter cash or cheque number"
                  />
                </div>

                <div>
                  <Label htmlFor="donation-payment-mode">Payment Mode *</Label>
                  <Select
                    value={form.payment_mode || undefined}
                    onValueChange={value =>
                      setForm(prev => ({ ...prev, payment_mode: value }))
                    }
                  >
                    <SelectTrigger id="donation-payment-mode">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neft">NEFT</SelectItem>
                      <SelectItem value="rtgs">RTGS</SelectItem>
                      <SelectItem value="imps">IMPS</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">CHEQUE</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}

                <div>
                  <Label htmlFor="donation-pan">PAN Number *</Label>
                  <Input
                    id="donation-pan"
                    value={form.pan_number}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        pan_number: e.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="PAN Number"
                    maxLength={10}
                  />
                </div>

                {/* <div>
                  <Label htmlFor="donation-message">
                    Message/Inquiry details
                  </Label>
                  <Textarea
                    id="donation-message"
                    value={form.message}
                    onChange={e =>
                      setForm(prev => ({ ...prev, message: e.target.value }))
                    }
                    placeholder="Message or note"
                    rows={2}
                  />
                </div> */}

                <div className="space-y-4 rounded-lg border p-4">
                  <h3 className="font-semibold">Address Information</h3>
                  <div>
                    <Label htmlFor="address-line-1">Address Line 1 *</Label>
                    <Input
                      id="address-line-1"
                      value={form.address_line1}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          address_line1: e.target.value,
                        }))
                      }
                      placeholder="Street address, P.O. box, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="address-line-2">Address Line 2</Label>
                    <Input
                      id="address-line-2"
                      value={form.address_line2}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          address_line2: e.target.value,
                        }))
                      }
                      placeholder="Apartment, suite, unit, building, floor, etc."
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Select
                        value={String(form.state) || undefined}
                        onValueChange={value =>
                          setForm(prev => ({
                            ...prev,
                            state: value,
                            district: "",
                            city: "",
                          }))
                        }
                      >
                        <SelectTrigger id="state">
                          <SelectValue
                            placeholder={
                              loadingStates ? "Loading..." : "Select State"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {states.map(s => (
                            <SelectItem key={s.id} value={String(s.id)}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="district">District *</Label>
                      <Select
                        value={String(form.district) || undefined}
                        disabled={!form.state || loadingDistricts}
                        onValueChange={value =>
                          setForm(prev => ({
                            ...prev,
                            district: value,
                            city: "",
                          }))
                        }
                      >
                        <SelectTrigger id="district">
                          <SelectValue
                            placeholder={
                              loadingDistricts
                                ? "Loading..."
                                : "Select District"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {districts.map(d => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Select
                        value={String(form.city) || undefined}
                        disabled={!form.district || loadingCities}
                        onValueChange={value =>
                          setForm(prev => ({ ...prev, city: value }))
                        }
                      >
                        <SelectTrigger id="city">
                          <SelectValue
                            placeholder={
                              loadingCities ? "Loading..." : "Select City"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map(c => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input
                        id="pincode"
                        value={form.pincode}
                        onChange={e =>
                          setForm(prev => ({
                            ...prev,
                            pincode: e.target.value.replace(/[^0-9]/g, ""),
                          }))
                        }
                        placeholder="6-digit Pincode"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="shrink-0 gap-2 border-t border-border bg-background px-6 py-4 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={submitLoading}>
                  {submitLoading ? (
                    <CustomLoader size="xs" />
                  ) : editing ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-donation"
            columns={tableColumns}
            data={donations}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No records yet. Create one to get started."
                : "No records found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent className="flex max-h-[min(90vh,520px)] max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md [&>button]:right-4 [&>button]:top-4">
          <DialogHeader className="shrink-0 space-y-0 border-b border-border bg-background px-6 py-4 pr-12 text-left">
            <DialogTitle className="text-lg">Delete Donation</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete donation from{" "}
                  <strong>{deleting.name}</strong>?
                </p>
              </div>
              <DialogFooter className="shrink-0 gap-2 border-t border-border bg-background px-6 py-4 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <CustomLoader size="xs" className="" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
